import { create } from 'zustand';
import type {
  ActionResult,
  CollectionCategory,
  DomainEvent,
  FurniturePlacement,
  PersistedGameState,
  QuestProgress,
  SaveData,
} from '../core/domain/types';
import type { Clock } from '../core/time/clock';
import { systemClock } from '../core/time/clock';
import {
  clothing,
  clothingById,
  cropById,
  crops,
  furniture,
  furnitureById,
  questById,
  quests,
} from '../data/config';
import { createInitialState } from '../data/initialState';
import { indexedDbSaveRepository, type SaveRepository } from '../data/repositories/saveRepository';
import {
  addInventory,
  addXp,
  canAfford,
  canClaimStarterSeedRelief,
  STARTER_RELIEF_SEEDS,
} from '../features/economy/domain/economyRules';
import {
  collectionProgress,
  COLLECTION_REWARDS,
  discoveredCollectionIds,
  HOUSE_LEVEL_IDS,
} from '../features/collection/domain/collectionRules';
import { effectiveNow, getPlotPhase } from '../features/farm/domain/farmRules';
import { findFirstSpace, isPlacementValid } from '../features/home/domain/furnitureRules';
import { applyQuestEvent, createQuestState, dateKey } from '../features/quest/domain/questRules';
import {
  advanceTutorial,
  completeTutorial,
  normalizeTutorialState,
  skipTutorialStep as advancePastTutorialStep,
} from '../features/tutorial/domain/tutorialRules';
import { equipClothing, randomOutfit } from '../features/wardrobe/domain/wardrobeRules';
import { audioEngine } from '../infrastructure/audio/audioEngine';
import { useUiStore } from './uiStore';

export interface GameActions {
  plantCrop(plotId: string, cropId: string): ActionResult;
  harvestCrop(plotId: string): ActionResult;
  sellCrop(cropId: string, quantity: number): ActionResult;
  buySeeds(cropId: string, quantity: number): ActionResult;
  claimStarterSeedRelief(): ActionResult;
  buyFurniture(furnitureId: string): ActionResult;
  buyClothing(clothingId: string): ActionResult;
  unlockNextPlot(): ActionResult;
  beginDecorating(): void;
  placeFurniture(furnitureId: string): ActionResult;
  moveFurniture(instanceId: string, gridX: number, gridY: number): ActionResult;
  rotateFurniture(instanceId: string): ActionResult;
  removeFurniture(instanceId: string): void;
  commitDecorating(): void;
  cancelDecorating(): void;
  previewClothing(clothingId: string): ActionResult;
  confirmOutfit(): void;
  cancelOutfit(): void;
  randomizeOutfit(): void;
  saveOutfit(): ActionResult;
  upgradeHouse(): ActionResult;
  claimQuest(questId: string): ActionResult;
  markCollectionSeen(itemId: string): void;
  claimCollectionReward(category: CollectionCategory): ActionResult;
  skipTutorialStep(): void;
  skipTutorial(): void;
  debugAddCoins(amount: number): void;
  debugAdvanceTime(milliseconds: number): void;
}

interface GameStore {
  game: PersistedGameState;
  status: 'loading' | 'ready' | 'error';
  loadError: string | null;
  actions: GameActions;
}

let activeClock: Clock = systemClock;
let saveRepository: SaveRepository = indexedDbSaveRepository;

function show(message: string): void {
  useUiStore.getState().showNotice(message);
}

function saveCriticalChange(): void {
  window.clearTimeout(saveTimer);
  saveTimer = undefined;
  void saveNow().catch(() => show('自动保存失败，请暂时不要关闭游戏。'));
}

function saveIfTutorialAdvanced(previousStep: number): void {
  const tutorial = useGameStore.getState().game.tutorial;
  if (tutorial.step !== previousStep) saveCriticalChange();
}

function collectionCatalogIds(category: CollectionCategory): string[] {
  if (category === 'crop') return crops.map((item) => item.id);
  if (category === 'furniture') return furniture.map((item) => item.id);
  if (category === 'clothing') return clothing.map((item) => item.id);
  return HOUSE_LEVEL_IDS;
}
function applyEvent(game: PersistedGameState, event: DomainEvent): PersistedGameState {
  const tutorial = advanceTutorial(game.tutorial, event);
  const collection = { ...game.collection };
  if (event.type === 'cropHarvested' && !collection.discoveredCropIds.includes(event.cropId)) {
    collection.discoveredCropIds = [...collection.discoveredCropIds, event.cropId];
  }
  if (
    event.type === 'furniturePlaced' &&
    !collection.discoveredFurnitureIds.includes(event.furnitureId)
  ) {
    collection.discoveredFurnitureIds = [...collection.discoveredFurnitureIds, event.furnitureId];
  }
  if (
    event.type === 'clothingOwned' &&
    !collection.discoveredClothingIds.includes(event.clothingId)
  ) {
    collection.discoveredClothingIds = [...collection.discoveredClothingIds, event.clothingId];
  }

  return {
    ...game,
    quests: applyQuestEvent(game.quests, event, questById),
    collection,
    tutorial,
  };
}

function warehouseCount(game: PersistedGameState): number {
  return Object.entries(game.inventory)
    .filter(([id]) => id.startsWith('crop_'))
    .reduce((sum, [, amount]) => sum + amount, 0);
}

function updateUnlocks(game: PersistedGameState): PersistedGameState {
  const unlockedCount = Math.min(24, 6 + Math.max(0, game.player.level - 1) * 2);
  return {
    ...game,
    farm: {
      ...game.farm,
      plots: game.farm.plots.map((plot, index) =>
        index < unlockedCount ? { ...plot, unlocked: true } : plot,
      ),
    },
  };
}

export const useGameStore = create<GameStore>((set, get) => {
  const replaceGame = (recipe: (game: PersistedGameState) => PersistedGameState): void =>
    set((state) => ({ game: recipe(state.game) }));

  const actions: GameActions = {
    plantCrop(plotId, cropId) {
      const state = get().game;
      const crop = cropById.get(cropId);
      const plot = state.farm.plots.find((item) => item.plotId === plotId);
      if (!crop || !plot) return { ok: false, error: 'invalid', message: '找不到这块农田或作物。' };
      if (!plot.unlocked || crop.unlockLevel > state.player.level)
        return { ok: false, error: 'locked', message: '这个内容还没有解锁。' };
      if (getPlotPhase(plot, crop, activeClock.nowUtc()) !== 'empty')
        return { ok: false, error: 'invalid', message: '这块田现在不能播种。' };
      const seedId = `seed_${cropId}`;
      if ((state.inventory[seedId] ?? 0) < 1)
        return { ok: false, error: 'not-enough-seeds', message: '种子不够，可以去商店购买。' };

      replaceGame((game) =>
        applyEvent(
          {
            ...game,
            inventory: addInventory(game.inventory, seedId, -1),
            farm: {
              ...game.farm,
              plots: game.farm.plots.map((item) =>
                item.plotId === plotId
                  ? { ...item, cropId, plantedAtUtc: activeClock.nowUtc() }
                  : item,
              ),
            },
          },
          { type: 'cropPlanted', cropId, count: 1 },
        ),
      );
      saveIfTutorialAdvanced(state.tutorial.step);
      audioEngine.play('plant');
      show(`种下了${crop.name}。`);
      return { ok: true };
    },

    harvestCrop(plotId) {
      const state = get().game;
      const plot = state.farm.plots.find((item) => item.plotId === plotId);
      const crop = plot?.cropId ? cropById.get(plot.cropId) : undefined;
      if (!plot || !crop) return { ok: false, error: 'invalid', message: '这里没有作物。' };
      if (getPlotPhase(plot, crop, activeClock.nowUtc()) !== 'mature')
        return { ok: false, error: 'not-mature', message: '作物还没有成熟。' };
      if (warehouseCount(state) >= state.player.warehouseCapacity)
        return { ok: false, error: 'warehouse-full', message: '仓库已经满了，请先出售作物。' };

      replaceGame((game) => {
        const player = addXp(game.player, crop.harvestXp);
        return updateUnlocks(
          applyEvent(
            {
              ...game,
              player,
              inventory: addInventory(game.inventory, crop.id, 1),
              farm: {
                ...game.farm,
                plots: game.farm.plots.map((item) =>
                  item.plotId === plotId
                    ? {
                        ...item,
                        cropId: null,
                        plantedAtUtc: null,
                        harvestCount: item.harvestCount + 1,
                      }
                    : item,
                ),
              },
            },
            { type: 'cropHarvested', cropId: crop.id, count: 1 },
          ),
        );
      });
      saveIfTutorialAdvanced(state.tutorial.step);
      audioEngine.play('harvest');
      show(`收获了${crop.name}，获得 ${crop.harvestXp} 经验。`);
      return { ok: true };
    },

    sellCrop(cropId, quantity) {
      const state = get().game;
      const crop = cropById.get(cropId);
      const count = Math.min(Math.max(0, Math.floor(quantity)), state.inventory[cropId] ?? 0);
      if (!crop || count < 1)
        return { ok: false, error: 'invalid', message: '没有可以出售的作物。' };
      const coins = crop.sellPrice * count;
      replaceGame((game) =>
        applyEvent(
          {
            ...game,
            player: { ...game.player, coins: game.player.coins + coins },
            inventory: addInventory(game.inventory, cropId, -count),
          },
          { type: 'itemSold', itemId: cropId, count, coins },
        ),
      );
      saveIfTutorialAdvanced(state.tutorial.step);
      audioEngine.play('coin');
      show(`出售 ${count} 个${crop.name}，获得 ${coins} 金币。`);
      return { ok: true };
    },

    buySeeds(cropId, quantity) {
      const state = get().game;
      const crop = cropById.get(cropId);
      const count = Math.max(1, Math.floor(quantity));
      if (!crop || crop.unlockLevel > state.player.level)
        return { ok: false, error: 'locked', message: '种子还没有解锁。' };
      const price = crop.seedPrice * count;
      if (!canAfford(state.player, price))
        return { ok: false, error: 'not-enough-coins', message: '金币不足。' };
      replaceGame((game) => ({
        ...game,
        player: { ...game.player, coins: game.player.coins - price },
        inventory: addInventory(game.inventory, `seed_${cropId}`, count),
      }));
      saveCriticalChange();
      show(`购买了 ${count} 颗${crop.name}种子。`);
      return { ok: true };
    },

    claimStarterSeedRelief() {
      const state = get().game;
      if (!canClaimStarterSeedRelief(state, crops))
        return {
          ok: false,
          error: 'invalid',
          message: '仍有可用资源，暂时不需要种子救济。',
        };
      replaceGame((game) => ({
        ...game,
        inventory: addInventory(game.inventory, 'seed_crop_radish', STARTER_RELIEF_SEEDS),
      }));
      saveCriticalChange();
      show('获得了 3 颗应急萝卜种子。');
      return { ok: true };
    },

    buyFurniture(furnitureId) {
      const state = get().game;
      const item = furnitureById.get(furnitureId);
      if (!item || item.unlockLevel > state.player.level)
        return { ok: false, error: 'locked', message: '家具还没有解锁。' };
      if (!canAfford(state.player, item.price))
        return { ok: false, error: 'not-enough-coins', message: '金币不足。' };
      replaceGame((game) => ({
        ...game,
        player: { ...game.player, coins: game.player.coins - item.price },
        home: {
          ...game.home,
          ownedFurniture: {
            ...game.home.ownedFurniture,
            [furnitureId]: (game.home.ownedFurniture[furnitureId] ?? 0) + 1,
          },
        },
      }));
      saveCriticalChange();
      show(`购买了${item.name}。`);
      return { ok: true };
    },

    buyClothing(clothingId) {
      const state = get().game;
      const item = clothingById.get(clothingId);
      if (!item || item.unlockLevel > state.player.level)
        return { ok: false, error: 'locked', message: '服装还没有解锁。' };
      if (state.wardrobe.ownedClothingIds.includes(clothingId))
        return { ok: false, error: 'already-owned', message: '已经拥有这件服装。' };
      if (!canAfford(state.player, item.price))
        return { ok: false, error: 'not-enough-coins', message: '金币不足。' };
      replaceGame((game) =>
        applyEvent(
          {
            ...game,
            player: { ...game.player, coins: game.player.coins - item.price },
            wardrobe: {
              ...game.wardrobe,
              ownedClothingIds: [...game.wardrobe.ownedClothingIds, clothingId],
            },
          },
          { type: 'clothingOwned', clothingId, count: 1 },
        ),
      );
      saveCriticalChange();
      show(`购买了${item.name}。`);
      return { ok: true };
    },

    unlockNextPlot() {
      const state = get().game;
      const index = state.farm.plots.findIndex((plot) => !plot.unlocked);
      if (index < 0) return { ok: false, error: 'invalid', message: '所有农田都已经解锁。' };
      const price = 100 + index * 20;
      if (!canAfford(state.player, price))
        return { ok: false, error: 'not-enough-coins', message: `需要 ${price} 金币。` };
      replaceGame((game) => ({
        ...game,
        player: { ...game.player, coins: game.player.coins - price },
        farm: {
          ...game.farm,
          plots: game.farm.plots.map((plot, plotIndex) =>
            plotIndex === index ? { ...plot, unlocked: true } : plot,
          ),
        },
      }));
      saveCriticalChange();
      return { ok: true };
    },

    beginDecorating() {
      replaceGame((game) => ({
        ...game,
        home: { ...game.home, draftPlacements: game.home.placements.map((item) => ({ ...item })) },
      }));
    },

    placeFurniture(furnitureId) {
      const state = get().game;
      const definition = furnitureById.get(furnitureId);
      if (!definition) return { ok: false, error: 'invalid', message: '找不到这件家具。' };
      const placements = state.home.draftPlacements ?? state.home.placements;
      const owned = state.home.ownedFurniture[furnitureId] ?? 0;
      const used = placements.filter((item) => item.furnitureId === furnitureId).length;
      if (used >= owned) return { ok: false, error: 'invalid', message: '没有未摆放的同款家具。' };
      const candidate = findFirstSpace(definition, definition.layer, placements, furnitureById);
      if (!candidate) return { ok: false, error: 'no-space', message: '房间里没有合适的位置。' };
      replaceGame((game) => ({
        ...game,
        home: {
          ...game.home,
          draftPlacements: [...(game.home.draftPlacements ?? game.home.placements), candidate],
        },
      }));
      return { ok: true };
    },

    moveFurniture(instanceId, gridX, gridY) {
      const state = get().game;
      const placements = state.home.draftPlacements ?? state.home.placements;
      const current = placements.find((item) => item.instanceId === instanceId);
      const definition = current ? furnitureById.get(current.furnitureId) : undefined;
      if (!current || !definition) return { ok: false, error: 'invalid', message: '找不到家具。' };
      const candidate = { ...current, gridX, gridY };
      if (!isPlacementValid(candidate, definition, placements, furnitureById))
        return { ok: false, error: 'no-space', message: '这里不能摆放家具。' };
      replaceGame((game) => ({
        ...game,
        home: {
          ...game.home,
          draftPlacements: (game.home.draftPlacements ?? game.home.placements).map((item) =>
            item.instanceId === instanceId ? candidate : item,
          ),
        },
      }));
      return { ok: true };
    },

    rotateFurniture(instanceId) {
      const state = get().game;
      const placements = state.home.draftPlacements ?? state.home.placements;
      const current = placements.find((item) => item.instanceId === instanceId);
      const definition = current ? furnitureById.get(current.furnitureId) : undefined;
      if (!current || !definition) return { ok: false, error: 'invalid', message: '找不到家具。' };
      const rotations = [0, 90, 180, 270] as const;
      const next = rotations[(rotations.indexOf(current.rotation) + 1) % rotations.length] ?? 0;
      const candidate: FurniturePlacement = { ...current, rotation: next };
      if (!isPlacementValid(candidate, definition, placements, furnitureById))
        return { ok: false, error: 'no-space', message: '旋转后会与其他家具重叠。' };
      replaceGame((game) => ({
        ...game,
        home: {
          ...game.home,
          draftPlacements: (game.home.draftPlacements ?? game.home.placements).map((item) =>
            item.instanceId === instanceId ? candidate : item,
          ),
        },
      }));
      return { ok: true };
    },

    removeFurniture(instanceId) {
      replaceGame((game) => ({
        ...game,
        home: {
          ...game.home,
          draftPlacements: (game.home.draftPlacements ?? game.home.placements).filter(
            (item) => item.instanceId !== instanceId,
          ),
        },
      }));
    },

    commitDecorating() {
      const state = get().game;
      if (!state.home.draftPlacements) return;
      const previousIds = new Set(state.home.placements.map((item) => item.instanceId));
      const additions = state.home.draftPlacements.filter(
        (item) => !previousIds.has(item.instanceId),
      );
      replaceGame((game) => {
        let updated: PersistedGameState = {
          ...game,
          home: {
            ...game.home,
            placements: game.home.draftPlacements ?? game.home.placements,
            draftPlacements: null,
          },
        };
        for (const item of additions) {
          updated = applyEvent(updated, {
            type: 'furniturePlaced',
            furnitureId: item.furnitureId,
            count: 1,
          });
        }
        return updated;
      });
      saveIfTutorialAdvanced(state.tutorial.step);
      audioEngine.play('build');
      show('装修布局已保存。');
    },

    cancelDecorating() {
      replaceGame((game) => ({ ...game, home: { ...game.home, draftPlacements: null } }));
    },

    previewClothing(clothingId) {
      const state = get().game;
      const definition = clothingById.get(clothingId);
      if (!definition || !state.wardrobe.ownedClothingIds.includes(clothingId))
        return { ok: false, error: 'locked', message: '还没有拥有这件服装。' };
      replaceGame((game) => ({
        ...game,
        wardrobe: equipClothing(game.wardrobe, definition, true),
      }));
      return { ok: true };
    },

    confirmOutfit() {
      const state = get().game;
      if (!state.wardrobe.preview) return;
      const changedId = Object.values(state.wardrobe.preview).find(
        (id) => !Object.values(state.wardrobe.equipped).includes(id),
      );
      replaceGame((game) => {
        let updated: PersistedGameState = {
          ...game,
          wardrobe: {
            ...game.wardrobe,
            equipped: game.wardrobe.preview ?? game.wardrobe.equipped,
            preview: null,
          },
        };
        if (changedId)
          updated = applyEvent(updated, {
            type: 'clothingEquipped',
            clothingId: changedId,
            count: 1,
          });
        return updated;
      });
      saveIfTutorialAdvanced(state.tutorial.step);
      audioEngine.play('outfit');
      show('新造型已经保存。');
    },

    cancelOutfit() {
      replaceGame((game) => ({ ...game, wardrobe: { ...game.wardrobe, preview: null } }));
    },

    randomizeOutfit() {
      replaceGame((game) => ({
        ...game,
        wardrobe: {
          ...game.wardrobe,
          preview: randomOutfit(game.wardrobe.ownedClothingIds, [...clothingById.values()]),
        },
      }));
    },

    saveOutfit() {
      const state = get().game;
      if (state.wardrobe.savedOutfits.length >= 5)
        return { ok: false, error: 'invalid', message: '最多保存5套搭配。' };
      replaceGame((game) => ({
        ...game,
        wardrobe: {
          ...game.wardrobe,
          savedOutfits: [
            ...game.wardrobe.savedOutfits,
            {
              id: crypto.randomUUID(),
              name: `搭配 ${game.wardrobe.savedOutfits.length + 1}`,
              equipped: { ...(game.wardrobe.preview ?? game.wardrobe.equipped) },
            },
          ],
        },
      }));
      saveCriticalChange();
      return { ok: true };
    },

    upgradeHouse() {
      const state = get().game;
      if (state.home.houseLevel >= 5)
        return { ok: false, error: 'invalid', message: '房屋已经达到最高等级。' };
      const costs = [0, 150, 400, 900, 1800];
      const requirements = [1, 2, 5, 8, 12];
      const nextLevel = state.home.houseLevel + 1;
      const price = costs[state.home.houseLevel] ?? 9999;
      if (state.player.level < (requirements[state.home.houseLevel] ?? 15))
        return { ok: false, error: 'locked', message: '玩家等级还不够。' };
      if (!canAfford(state.player, price))
        return { ok: false, error: 'not-enough-coins', message: `需要 ${price} 金币。` };
      replaceGame((game) =>
        applyEvent(
          {
            ...game,
            player: { ...game.player, coins: game.player.coins - price },
            home: { ...game.home, houseLevel: nextLevel },
          },
          { type: 'houseUpgraded', level: nextLevel, count: 1 },
        ),
      );
      saveCriticalChange();
      return { ok: true };
    },

    claimQuest(questId) {
      const state = get().game;
      const definition = questById.get(questId);
      const locate = (items: QuestProgress[]) => items.find((item) => item.questId === questId);
      const entry = locate(state.quests.daily) ?? locate(state.quests.growth);
      if (!definition || !entry || entry.claimed || entry.progress < definition.target)
        return { ok: false, error: 'invalid', message: '任务还不能领取。' };
      replaceGame((game) => {
        const mark = (items: QuestProgress[]) =>
          items.map((item) => (item.questId === questId ? { ...item, claimed: true } : item));
        return updateUnlocks({
          ...game,
          player: addXp(
            { ...game.player, coins: game.player.coins + definition.rewardCoins },
            definition.rewardXp,
          ),
          quests: {
            ...game.quests,
            daily: mark(game.quests.daily),
            growth: mark(game.quests.growth),
          },
        });
      });
      saveCriticalChange();
      return { ok: true };
    },

    claimCollectionReward(category) {
      const state = get().game;
      const progress = collectionProgress(state, category, collectionCatalogIds(category));
      if (progress.claimed)
        return { ok: false, error: 'invalid', message: '这个图鉴奖励已经领取过了。' };
      if (!progress.complete)
        return { ok: false, error: 'invalid', message: '收集完成后才能领取奖励。' };
      const reward = COLLECTION_REWARDS[category];
      replaceGame((game) =>
        updateUnlocks({
          ...game,
          player: addXp({ ...game.player, coins: game.player.coins + reward.coins }, reward.xp),
          collection: {
            ...game.collection,
            claimedCategoryRewardIds: [...game.collection.claimedCategoryRewardIds, category],
          },
        }),
      );
      saveCriticalChange();
      return { ok: true, message: '图鉴奖励领取成功！' };
    },

    markCollectionSeen(itemId) {
      const state = get().game;
      const categories: CollectionCategory[] = ['crop', 'furniture', 'clothing', 'house'];
      const discovered = categories.some((category) =>
        discoveredCollectionIds(state, category).includes(itemId),
      );
      if (!discovered || state.collection.seenIds.includes(itemId)) return;
      replaceGame((game) => ({
        ...game,
        collection: {
          ...game.collection,
          seenIds: [...game.collection.seenIds, itemId],
        },
      }));
    },

    skipTutorialStep() {
      replaceGame((game) => ({
        ...game,
        tutorial: advancePastTutorialStep(game.tutorial),
      }));
      saveCriticalChange();
    },

    skipTutorial() {
      replaceGame((game) => ({ ...game, tutorial: completeTutorial() }));
      saveCriticalChange();
    },

    debugAddCoins(amount) {
      if (!import.meta.env.DEV) return;
      replaceGame((game) => ({
        ...game,
        player: { ...game.player, coins: Math.max(0, game.player.coins + amount) },
      }));
    },

    debugAdvanceTime(milliseconds) {
      if (!import.meta.env.DEV) return;
      replaceGame((game) => ({
        ...game,
        farm: {
          ...game.farm,
          plots: game.farm.plots.map((plot) =>
            plot.plantedAtUtc === null
              ? plot
              : { ...plot, plantedAtUtc: plot.plantedAtUtc - milliseconds },
          ),
        },
      }));
    },
  };

  return { game: createInitialState(), status: 'loading', loadError: null, actions };
});

function createSave(game: PersistedGameState): SaveData {
  const now = activeClock.nowUtc();
  const snapshot = { ...game, lastSavedAtUtc: now, farm: { ...game.farm, lastSeenUtc: now } };
  return {
    schemaVersion: 2,
    saveId: 'main',
    createdAtUtc: game.createdAtUtc,
    lastSavedAtUtc: now,
    game: snapshot,
  };
}

export async function hydrateGame(): Promise<void> {
  try {
    const main = await saveRepository.loadMain();
    const backup = main ? null : await saveRepository.loadBackup();
    const loaded = main ?? backup;
    const now = activeClock.nowUtc();
    const game = loaded?.game ?? createInitialState(now);
    const effective = effectiveNow(game.farm.lastSeenUtc, now);
    const refreshedQuests =
      game.quests.dateKey === dateKey(now) ? game.quests : createQuestState(now, quests);
    useGameStore.setState({
      game: {
        ...game,
        farm: { ...game.farm, lastSeenUtc: effective },
        quests: refreshedQuests,
        tutorial: normalizeTutorialState(game.tutorial),
      },
      status: 'ready',
      loadError: null,
    });
    if (!main && backup) show('主存档不可用，已从备份恢复。');
  } catch (error) {
    useGameStore.setState({
      status: 'error',
      loadError: error instanceof Error ? error.message : '存档加载失败',
    });
  }
}

let saveTimer: number | undefined;
let saveQueue: Promise<void> = Promise.resolve();
export function installAutosave(): () => void {
  return useGameStore.subscribe((state, previous) => {
    if (state.status !== 'ready' || state.game === previous.game) return;
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => {
      void saveNow().catch(() => show('自动保存失败，请暂时不要关闭游戏。'));
    }, 1200);
  });
}

export async function saveNow(): Promise<void> {
  if (useGameStore.getState().status !== 'ready') return;
  const snapshot = createSave(useGameStore.getState().game);
  const repository = saveRepository;
  const nextSave = saveQueue.catch(() => undefined).then(() => repository.save(snapshot));
  saveQueue = nextSave;
  await nextSave;
}

export function setGameDependenciesForTests(clock: Clock, repository?: SaveRepository): void {
  activeClock = clock;
  if (repository) saveRepository = repository;
}

export function resetGameDependencies(): void {
  activeClock = systemClock;
  saveRepository = indexedDbSaveRepository;
  saveQueue = Promise.resolve();
  window.clearTimeout(saveTimer);
  saveTimer = undefined;
}
